import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;
  #historyBackPending = false;
  #historyScrollY;
  #historyUrl;
  #scrollRestoreVersion = 0;
  #scrollRestoreTimer;
  #scrollRestoreController;
  #historyCloseStyleOwner;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (this.#historyBackPending && !history.state?.cartDrawerOpen) this.#historyBackPending = false;
    if (this.#historyBackPending) {
      this.#listenForHistory();
    } else if (history.state?.cartDrawerOpen) {
      const { cartDrawerOpen, ...otherState } = history.state;
      history.replaceState(otherState, '');
    }
  }

  disconnectedCallback() {
    this.#cancelScrollRestore();
    this.#historyScrollY = undefined;
    this.#historyUrl = undefined;
    this.#historyCloseStyleOwner?.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    this.#historyCloseStyleOwner = undefined;
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
  }

  #handleHistoryOpen = () => {
    this.#cancelScrollRestore();
    this.#historyScrollY = this.scrollLockY;
    this.#historyUrl = window.location?.href;
    if (!isMobileBreakpoint() || this.#historyBackPending) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ ...history.state, cartDrawerOpen: true }, '');
    }

    this.#listenForHistory();
  };

  #listenForHistory() {
    this.#historyAbortController?.abort();
    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  }

  #handleHistoryClose = () => {
    if (this.#historyBackPending) return;
    if (history.state?.cartDrawerOpen) {
      this.#historyBackPending = true;
      history.back();
    } else {
      this.#historyAbortController?.abort();
    }
  };

  #handlePopState = async () => {
    this.#cancelScrollRestore();
    const version = this.#scrollRestoreVersion;
    const position = this.#historyScrollY;
    const url = window.location?.href;
    const state = history.state;
    const entryKey = window.navigation?.currentEntry?.key;
    if ((this.#historyBackPending || this.refs.dialog?.open) && url === this.#historyUrl) {
      this.#watchScrollRestore();
    }
    if (this.#historyBackPending) {
      this.#historyBackPending = false;
      this.#historyAbortController?.abort();
      if (this.refs.dialog?.open) this.#handleHistoryOpen();
      else this.#restoreScrollAfterTraversal({ version, position, url, state, entryKey });
      return;
    }
    if (this.refs.dialog?.open) {
      const { dialog } = this.refs;
      const closeStyleOwner = { dialog };
      this.#historyCloseStyleOwner = closeStyleOwner;
      dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      try {
        await this.closeDialog();
        this.#restoreScrollAfterTraversal({ version, position, url, state, entryKey });
      } catch (error) {
        if (version === this.#scrollRestoreVersion) this.#cancelScrollRestore();
        throw error;
      } finally {
        if (this.#historyCloseStyleOwner === closeStyleOwner) {
          dialog.style.removeProperty('--dialog-drawer-closing-animation');
          this.#historyCloseStyleOwner = undefined;
        }
      }
    }
  };

  #cancelScrollRestore = () => {
    this.#scrollRestoreVersion++;
    if (this.#scrollRestoreTimer !== undefined) clearTimeout(this.#scrollRestoreTimer);
    this.#scrollRestoreTimer = undefined;
    this.#scrollRestoreController?.abort();
    this.#scrollRestoreController = undefined;
  };

  #watchScrollRestore() {
    const controller = new AbortController();
    this.#scrollRestoreController = controller;
    const { signal } = controller;
    window.addEventListener('pagehide', this.#cancelScrollRestore, { signal });
    window.addEventListener('popstate', this.#cancelScrollRestore, { signal });
    document.addEventListener(DialogOpenEvent.eventName, this.#cancelScrollRestore, { capture: true, signal });
    for (const type of ['pointerdown', 'keydown', 'wheel', 'touchstart']) {
      document.addEventListener(type, this.#cancelScrollRestore, { capture: true, passive: true, signal });
    }
  }

  #restoreScrollAfterTraversal({ version, position, url, state, entryKey }) {
    if (version !== this.#scrollRestoreVersion) return;
    if (!Number.isFinite(position)) { this.#cancelScrollRestore(); return; }
    const controller = this.#scrollRestoreController;
    if (!controller || controller.signal.aborted) return;

    // The browser restores persisted history scroll after popstate. Repair only
    // this completed drawer traversal, without changing scrollRestoration.
    this.#scrollRestoreTimer = setTimeout(() => {
      this.#scrollRestoreTimer = undefined;
      controller.abort();
      if (this.#scrollRestoreController === controller) this.#scrollRestoreController = undefined;
      if (version !== this.#scrollRestoreVersion || !this.isConnected || this.refs.dialog?.open ||
        document.querySelector('dialog[open]') || document.body.style.position === 'fixed' ||
        document.documentElement.hasAttribute('scroll-lock') ||
        window.location?.href !== url || history.state !== state ||
        (entryKey !== undefined && window.navigation?.currentEntry?.key !== entryKey)) return;
      window.scrollTo({ top: position, behavior: 'instant' });
    }, 0);
  }

  /** @param {HTMLElement | Event} [returnFocusTarget] */
  showDialog(returnFocusTarget) {
    this.#cancelScrollRestore();
    super.showDialog(returnFocusTarget);
  }

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleCartAdd = (event) => {
    if (event instanceof CartAddEvent && this.hasAttribute('auto-open')) {
      this.showDialog(event.detail.data?.returnFocusTarget);
    }

    this.#announceCartCount(event.detail.resource?.item_count);
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
